import React, { useState, useRef } from 'react';
import './admin.css';

/**
 * Compress and resize image to reduce storage size
 * @param {File} file - Original file
 * @param {number} maxWidth - Maximum width (default 800px)
 * @param {number} quality - JPEG quality 0-1 (default 0.7)
 * @returns {Promise<string>} - Compressed base64 string
 */
const compressImage = (file, maxWidth = 600, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if larger than maxWidth
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to compressed JPEG
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        
        console.log(`Image compressed: ${Math.round(file.size/1024)}KB → ${Math.round(compressedBase64.length/1024)}KB`);
        
        resolve(compressedBase64);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const ImageUploader = ({ currentImage, onUpload, folder = 'udad' }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || '');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      // Compress and convert to base64
      const compressedBase64 = await compressImage(file);
      
      // Check if compressed size is reasonable (< 50KB)
      if (compressedBase64.length > 50000) {
        console.warn('Image still large after compression, reducing quality...');
        // Try with lower quality
        const moreCompressed = await compressImage(file, 400, 0.4);
        setPreview(moreCompressed);
        onUpload(moreCompressed);
      } else {
        setPreview(compressedBase64);
        onUpload(compressedBase64);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'upload');
      setPreview(currentImage || '');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onUpload(url);
  };

  const handleClear = () => {
    setPreview('');
    onUpload('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="admin-image-uploader">
      {preview && (
        <div className="admin-image-preview">
          <img src={preview} alt="Preview" />
          <button 
            type="button" 
            className="admin-image-clear" 
            onClick={handleClear}
            title="Supprimer l'image"
          >
            ✕
          </button>
        </div>
      )}

      <div className="admin-upload-controls">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          style={{ display: 'none' }}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="admin-btn admin-btn-secondary"
          disabled={uploading}
        >
          {uploading ? 'Compression...' : '📁 Choisir une image'}
        </button>

        <span className="admin-upload-or">ou</span>

        <input
          type="url"
          value={preview.startsWith('data:') ? '' : preview}
          onChange={handleUrlChange}
          placeholder="Coller l'URL de l'image"
          className="admin-url-input"
        />
      </div>

      <p className="admin-upload-hint">
        💡 Conseil : Utilisez une URL externe (assets/, https://...) pour éviter les problèmes de stockage
      </p>

      {error && <p className="admin-upload-error">{error}</p>}
    </div>
  );
};

export default ImageUploader;
